import React, { useRef, useState } from 'react';
import { Typography, useTheme, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Fund, Courtage } from '../../api/useFunds';
import { CloseIcon } from '../../components/CloseIcon';
import { toEuroWholeNumber, toPercent, toPercentWholeNumber } from '../../utils/numberFormat';
import { bicToName } from '../../utils/bicMappings';
import {
  VlIcon,
  Container,
  StyledTextField,
  SubLabelText,
  SpacerRightXs,
  YesIcon,
  WarnIcon,
  CenteredDesktopOnly,
  Heading,
  HeadingContainer,
  LabelText,
  LightTooltip,
  FundNotAvailableWarning,
  Centered,
  Commission,
} from './components';

export interface FundInputProps {
  fund: Fund;
  investedAmount: number;
  bic: Bic | 'OTHER_BANK';
  isCollapsed: boolean;
  onChange: (fund: Fund, amount: number) => void;
  onRemove: (fund: Fund) => void;
  isAddedExternally?: boolean;
}

export const FundInput = ({
  fund,
  investedAmount,
  bic,
  isCollapsed,
  onChange,
  onRemove,
}: FundInputProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const ref = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const compareBic = bic === 'OTHER_BANK' ? 'COBADEHDXXX' : bic;

  const courtage = fund.courtages?.find((c: Courtage) => {
    return c.bic === compareBic;
  });

  const agio = courtage?.agio && investedAmount ? courtage.agio * investedAmount : 0;

  const portfolioCommission =
    courtage?.portfolioCommission && investedAmount
      ? courtage.portfolioCommission * investedAmount
      : 0;

  const handleAmountChange = (event: any) => {
    try {
      const amount = parseInt(event.target.value, 10);
      onChange(fund, amount);
    } catch (err) {
      onChange(fund, 0);
    }
  };

  const handleOpenOrClose = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box sx={{ marginTop: theme.spacing(4.5) }}>
      <HeadingContainer ref={ref}>
        <Heading variant="body1" onClick={() => handleOpenOrClose()}>
          {fund.name} ({fund.isin})
          {isCollapsed && !isOpen && investedAmount && (
            <span>; {toEuroWholeNumber(investedAmount)}</span>
          )}
        </Heading>
        <CloseIcon
          data-testid={`remove-fund-${fund.isin}`}
          onClick={() => {
            onRemove(fund);
          }}
        />
      </HeadingContainer>
      {!courtage && (
        <FundNotAvailableWarning data-testid="fund-not-available" variant="body2">
          <WarnIcon
            sx={{ fontSize: '20px', marginRight: '5px' }}
            data-testid="fund-not-available-warning"
          />
          {t('cashbackCalculator.detailed.fundInput.notAvailableAt', {
            bank: bicToName(bic),
          })}
        </FundNotAvailableWarning>
      )}
      {(!isCollapsed || isOpen) && (
        <Container>
          <Box sx={{ maxWidth: '380px', flexGrow: 1 }}>
            <Typography
              color="textSecondary"
              component="label"
              variant="body2"
              id="partner-bank-selector-label"
              sx={{ display: 'block', marginBottom: theme.spacing(2) }}
            >
              {t('cashbackCalculator.detailed.fundInput.amount')}
            </Typography>
            <StyledTextField
              fullWidth
              id={`fund-amount-${fund.isin}`}
              inputProps={{
                'data-testid': 'fund-amount-input',
                'data-hj-whitelist': true,
              }}
              variant="outlined"
              type="number"
              defaultValue={investedAmount ? investedAmount?.toString() : undefined}
              onChange={handleAmountChange}
            />
          </Box>
          <Centered sx={{ minWidth: '135px' }}>
            <SpacerRightXs>
              <LabelText data-testid="agio-in-percent">
                {t('cashbackCalculator.detailed.fundInput.agio')} (
                {toPercent(courtage?.agio ? courtage.agio : 0, 0, 1)})
              </LabelText>
            </SpacerRightXs>
            <SpacerRightXs>
              <Commission data-testid="agio-amount">{toEuroWholeNumber(agio)}</Commission>
            </SpacerRightXs>
            {!!courtage?.agio && (
              <SubLabelText
                variant="caption"
                color={courtage?.discountOnAgio === 1 ? 'success.dark' : 'common.Y500'}
              >
                {courtage?.discountOnAgio === 1 ? (
                  <YesIcon style={{ fontSize: '20px' }} data-testid="full-agio" />
                ) : (
                  <LightTooltip
                    placement="top"
                    title={t('cashbackCalculator.detailed.fundInput.limitedAgioDiscount') || ''}
                  >
                    <WarnIcon style={{ fontSize: '20px' }} data-testid="agio-warning" />
                  </LightTooltip>
                )}
                &nbsp;
                {t('cashbackCalculator.detailed.fundInput.agioDiscount', {
                  percent: toPercentWholeNumber(courtage?.discountOnAgio || 0).replace(' ', ''),
                })}
              </SubLabelText>
            )}
          </Centered>
          <Centered>
            <SpacerRightXs>
              <LabelText data-testid="portfolio-commission-in-percent">
                {t('cashbackCalculator.detailed.fundInput.portfolioCommission')} (
                {toPercent(courtage?.portfolioCommission ? courtage.portfolioCommission : 0)})
              </LabelText>
            </SpacerRightXs>
            <Commission data-testid="portfolio-commission-amount" color="textPrimary">
              {toEuroWholeNumber(portfolioCommission)}
            </Commission>
          </Centered>

          <Centered>
            {courtage?.hasSavingsPlan && courtage?.savingsPlanMinAmount && (
              <LightTooltip
                placement="top"
                title={
                  t('cashbackCalculator.detailed.fundInput.savingsPlanTooltip', {
                    from: toEuroWholeNumber(courtage.savingsPlanMinAmount),
                    bank: bicToName(bic),
                  }) || ''
                }
              >
                <LabelText style={{ cursor: 'pointer' }}>
                  {t('cashbackCalculator.detailed.fundInput.savingsPlan')}
                </LabelText>
              </LightTooltip>
            )}
            {!(courtage?.hasSavingsPlan && courtage?.savingsPlanMinAmount) && (
              <LabelText>{t('cashbackCalculator.detailed.fundInput.savingsPlan')}</LabelText>
            )}
            {courtage?.hasSavingsPlan ? (
              <CheckCircleIcon data-testid="has-sp" sx={{ color: theme.palette.icon.success }} />
            ) : (
              <Typography color="common.N400" fontSize="18px" data-testid="no-sp">
                -
              </Typography>
            )}
          </Centered>
          <CenteredDesktopOnly>
            <VlIcon courtage={courtage} testId="vl" />
          </CenteredDesktopOnly>
          <CenteredDesktopOnly>
            <div></div>
          </CenteredDesktopOnly>
        </Container>
      )}
    </Box>
  );
};
